import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { tagsOptions } from "../shared/utils/utils";
import { LoadingScreen } from "./loader/LoadingScreen";
import Multiselect from "multiselect-react-dropdown";
import { getClientsList } from "../services/client";

const getWasteCategoryId = (payload) => {
  if (!payload || payload === "" || payload === "{}") return null;
  try {
    return JSON.parse(payload)?.id ?? null;
  } catch {
    return null;
  }
};

const ContainerModalClient = ({
  data,
  displayClient,
  setDisplayClient,
  weightRef,
  handleSelectionChange,
  clientCode,
  setClientCode,
  weight,
  setWeight,
  wasteCategoryName,
  setWasteCategoryName,
  wasteCategory,
  saveWeight,
  totalWeight,
  superContainer,
  isCharacterization,
  refreshAction,
  loading,
  tags,
  setTags,
  displayStatus,
  setDisplayStatus,
  displayTags,
  setDisplayTags,
  wasteName,
  clientId,
}) => {
  const { t } = useTranslation();
  const [clientList, setClientList] = useState([]);
  const [changeWasteCategory, setChangeWasteCategory] = useState(false);
  // Snapshot of denomination at modal open (updated while hydrating, frozen after user change).
  const initialWasteCategoryIdRef = useRef(null);

  const dataId = data?._id;
  const superContainerId = superContainer?._id;

  const containerId = useMemo(
    () =>
      superContainerId !== undefined && superContainerId !== null
        ? superContainerId
        : dataId,
    [dataId, superContainerId],
  );

  const fetchContainerClient = useCallback(async () => {
    try {
      const response = await getClientsList();
      const clients = response?.data?.data ?? [];
      const dedupedClients = clients.reduce(
        (acc, client) => {
          const key = `${client.name}-${client.address}`;
          if (!acc.keys.has(key)) {
            acc.keys.add(key);
            acc.items.push(client);
          }
          return acc;
        },
        { keys: new Set(), items: [] },
      ).items;

      setClientList(dedupedClients);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch clients", error);
    }
  }, []);

  useEffect(() => {
    fetchContainerClient();
  }, [fetchContainerClient, displayClient, containerId]);

  useEffect(() => {
    if (!displayClient) {
      initialWasteCategoryIdRef.current = null;
      setChangeWasteCategory(false);
      return;
    }
    if (!changeWasteCategory) {
      initialWasteCategoryIdRef.current = getWasteCategoryId(wasteCategoryName);
    }
  }, [displayClient, wasteCategoryName, changeWasteCategory]);

  const safeWasteCategory = useMemo(() => {
    if (!wasteCategoryName) {
      return null;
    }
    try {
      const parsed = JSON.parse(wasteCategoryName);
      return parsed?.wasteCategoryName ? parsed : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Invalid waste category payload", error);
      return null;
    }
  }, [wasteCategoryName]);

  useEffect(() => {
    if (safeWasteCategory || !wasteName) {
      return;
    }
    const category = wasteCategory.find((item) => item.label === wasteName);
    if (!category) {
      return;
    }

    const formattedCategory = JSON.stringify({
      id: category._id,
      wasteCategoryName: category.label,
      wasteCategoryCode: category.wasteCodeFull,
      wasteCategoryValue: category.value,
      wasteCategoryAuthorizedMaterial: category.authorizedMaterial,
      wasteCategoryDensity: category.density,
    });
    setWasteCategoryName(formattedCategory);
  }, [safeWasteCategory, wasteName, wasteCategory, setWasteCategoryName]);

  const clientOptions = useMemo(
    () =>
      clientList.map((item) => ({
        key: item._id ?? `${item.name}-${item.address}`,
        value: JSON.stringify(item),
        label: `${item.name} (${item?.address && item?.address !== " " && ![undefined, "undefined"].includes(item.address) ? item.address : "-"})`,
      })),
    [clientList],
  );

  const selectedClient = useMemo(() => {
    const match = clientList.find((item) => item._id === clientId);
    return match ? JSON.stringify(match) : "";
  }, [clientList, clientId]);

  const wasteOptions = useMemo(
    () =>
      wasteCategory.map((item) => ({
        key: item._id,
        value: JSON.stringify({
          id: item._id,
          wasteCategoryName: item.label,
          wasteCategoryCode: item.wasteCodeFull,
          wasteCategoryValue: item.value,
          wasteCategoryAuthorizedMaterial: item.authorizedMaterial,
          wasteCategoryDensity: item.density,
        }),
        label: item.label,
      })),
    [wasteCategory],
  );

  const selectedWasteCategory = safeWasteCategory
    ? JSON.stringify(safeWasteCategory)
    : "";

  const modalTitle = useMemo(() => {
    if (isCharacterization) {
      return t("Characterization");
    }
    if (displayStatus) {
      return t("Status");
    }
    if (displayTags) {
      return t("tags.title");
    }
    return t("Weight and producer");
  }, [isCharacterization, displayStatus, displayTags, t]);

  const handleModalClose = useCallback(() => {
    if (!isCharacterization) {
      setDisplayTags(false);
      setDisplayStatus(false);
      if (refreshAction) {
        refreshAction();
      }
    }
    setDisplayClient(false);
    setChangeWasteCategory(false);
  }, [
    isCharacterization,
    refreshAction,
    setDisplayTags,
    setDisplayStatus,
    setDisplayClient,
  ]);

  const fieldClassName =
    "bg-white border border-[#D3D3D3] text-[#222222] text-sm rounded-lg focus:ring-[#00A7A6] focus:border-[#00A7A6] block w-full min-h-[44px] px-3 py-2.5 leading-normal";
  const selectClassName = `${fieldClassName} appearance-auto`;

  return (
    <Modal
      key={containerId}
      ref={weightRef}
      isOpen={displayClient}
      toggle={handleModalClose}
      contentClassName="rounded-lg char-modal-content"
      style={{ maxWidth: "480px" }}
      centered
    >
      <ModalHeader
        className="char-modal-header"
        close={
          <button
            type="button"
            onClick={handleModalClose}
            className="char-modal-header-close"
            aria-label={t("Close")}
          >
            <i className="fas fa-times char-modal-header-close-icon" />
          </button>
        }
      >
        <span className="char-modal-header-title">{modalTitle}</span>
      </ModalHeader>
      <ModalBody className="char-modal-body">
        <div className="flex flex-col gap-4 px-6 pb-2 pt-1">
          {!displayTags && !displayStatus && (
            <>
              <p className="m-0 text-sm text-[#6B7280]">
                {t("clientModal.batchSaveHint")}
              </p>
              <div className="flex flex-col gap-1">
                <label
                  className="text-sm font-medium text-[#222222]"
                  htmlFor="client"
                >
                  {t("Waste Producers")}
                </label>
                <select
                  id="client"
                  className={selectClassName}
                  onChange={handleSelectionChange}
                  value={selectedClient}
                >
                  <option value="">{t("Select waste producer")}</option>
                  {clientOptions.map((option) => (
                    <option key={option.key} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Link
                  to={{ pathname: "/settings/clients" }}
                  target="_blank"
                  className="text-sm italic text-[#00A7A6] hover:underline mt-1"
                >
                  {t("add_new_waste_producer")}
                </Link>
              </div>
              <div className="flex flex-col gap-1">
                <label
                  className="text-sm font-medium text-[#222222]"
                  htmlFor="code"
                >
                  {t("Identification reference")}
                </label>
                <input
                  onChange={(event) => setClientCode(event.target.value)}
                  value={clientCode ?? ""}
                  type="text"
                  id="code"
                  className={fieldClassName}
                  required
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  className="text-sm font-medium text-[#222222]"
                  htmlFor="weight"
                >
                  {t("Weight")} {`(${t("tons")})`}
                </label>
                <input
                  onChange={(event) => setWeight(event.target.value)}
                  value={weight ? weight : ""}
                  type="text"
                  id="weight"
                  className={fieldClassName}
                  required
                />
              </div>
              <div className="flex flex-col gap-1">
                <label
                  className="text-sm font-medium text-[#222222]"
                  htmlFor="waste"
                >
                  {t("Waste name")}
                </label>
                <select
                  id="waste"
                  className={selectClassName}
                  value={selectedWasteCategory}
                  onChange={(event) => {
                    setWasteCategoryName(event.target.value);
                    setChangeWasteCategory(true);
                  }}
                >
                  <option value="">---- {t("Please select")} ----</option>
                  {wasteOptions.map((option) => (
                    <option key={option.key} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Link
                  to={{ pathname: "/waste" }}
                  target="_blank"
                  className="text-sm italic text-[#00A7A6] hover:underline mt-1"
                >
                  {t("Set up a new waste category")}
                </Link>
              </div>
            </>
          )}
          {!isCharacterization && displayTags && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#222222]">
                {t("tags.title")}
              </label>
              <Multiselect
                options={tagsOptions.map((tag) => ({
                  ...tag,
                  label: t(`tags.${tag.key}`),
                }))}
                displayValue="label"
                onSelect={setTags}
                onRemove={setTags}
                placeholder={t("tags.placeholder")}
                showArrow
                hidePlaceholder
                selectedValues={tags}
              />
            </div>
          )}
        </div>
      </ModalBody>
      {!data?.fictiveContainer?.active && (
        <ModalFooter className="char-modal-footer">
          <button
            type="button"
            onClick={handleModalClose}
            className="char-button"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="char-button char-button-primary"
            onClick={async () => {
              const currentId = getWasteCategoryId(wasteCategoryName);
              const initialId = initialWasteCategoryIdRef.current;
              const hasFluxChanged =
                changeWasteCategory ||
                (currentId != null && currentId !== initialId);
              await saveWeight(containerId, hasFluxChanged);
              setChangeWasteCategory(false);
            }}
          >
            {(totalWeight !== "" && !totalWeight) || displayClient
              ? t("Save")
              : t("Update")}
          </button>
        </ModalFooter>
      )}
      {loading && <LoadingScreen />}
    </Modal>
  );
};

export default ContainerModalClient;
